import * as PIXI from 'pixi.js';
import isEqual from 'lodash/isEqual';
import { getAttributesToSerialize, WatermarkAttribute } from '../attributes';
import { WatermarkPositions } from '../../enums';
import { TimeInvariantBase } from '../TimeInvariantBase';
import { Config } from '../../video-data/config';
import type { MediaSourceData } from '../asset/MediaAsset';
import type { WatermarkThemeRef } from '../attributes/template/common';
import { assertInArray } from '../../validators';
import { getReferenceResolution } from '../../libs/videoData';
import type { Scene } from '../scene/Scene';
import { TimeAwareMediaAsset } from '../asset/TimeAwareMediaAsset';
import { VideoData } from '../../video-data';
import type { AspectRatio } from '../../aspectRatioConfig';
import type { BaseSettings } from '../TimeAwareBase';

export type SceneWatermarkManagerSettings = BaseSettings & {
    getAspectRatio: () => AspectRatio;
};

type SourceDataOrInherit =
    | MediaSourceData
    | typeof Config.INHERIT_FROM_TEMPLATE
    | typeof Config.WATERMARK_NONE
    | WatermarkThemeRef;
export type SceneWatermarkPosition = WatermarkPositions | typeof Config.INHERIT_FROM_TEMPLATE;

const normalizeWatermarkSourceData = (sourceData: MediaSourceData): object => {
    const normalized: Partial<MediaSourceData> & Record<string, unknown> = {
        ...sourceData,
    };

    delete normalized.attribution;
    delete normalized.transform;
    delete normalized.type;
    delete normalized.uuid;
    delete normalized.title;

    return normalized;
};

const isSourceDataEqual = (sourceDataA: MediaSourceData, sourceDataB: MediaSourceData): boolean => {
    return isEqual(
        normalizeWatermarkSourceData(sourceDataA),
        normalizeWatermarkSourceData(sourceDataB),
    );
};

/**
 * This class manages the watermark on the scene level and overrides any template-level settings.
 * The template-level settings are found in luminary.template.watermark, which is a TemplateWatermarkSettings.
 */
export class SceneWatermarkManager extends TimeInvariantBase {
    override _classType: string = 'SceneWatermarkManager';

    @WatermarkAttribute({ supportsInherit: true })
    accessor sourceData: SourceDataOrInherit = Config.INHERIT_FROM_TEMPLATE;
    #position: SceneWatermarkPosition = Config.INHERIT_FROM_TEMPLATE;
    #positionBeforeBump: SceneWatermarkPosition = null;
    #getAspectRatio: () => AspectRatio;

    constructor(settings: SceneWatermarkManagerSettings) {
        super(settings);
        this.#getAspectRatio = settings.getAspectRatio;
    }

    get #templateWatermarkPosition() {
        return this.template.watermark.position;
    }

    /**
     * The position (corner) of the watermark
     */
    get position(): SceneWatermarkPosition {
        if (this.#position === Config.INHERIT_FROM_TEMPLATE) {
            return this.#templateWatermarkPosition;
        }
        return this.#position;
    }

    get rawPosition(): SceneWatermarkPosition {
        return this.#positionBeforeBump ?? this.#position;
    }

    set position(position: SceneWatermarkPosition) {
        if (this.#position === position) {
            return;
        }
        const validPositions = [...this.availablePositions, Config.INHERIT_FROM_TEMPLATE];
        assertInArray(position, validPositions);
        // Reset position before bump so that we don't jump to the previous position or save the previous posit
        this.#positionBeforeBump = null;
        this.#position = position;
        this.requiresUpdate = true;
    }

    /**
     * List of allowed watermark position for this scene
     */
    get availablePositions(): SceneWatermarkPosition[] {
        const textAssets = (this.parent as Scene)
            .getAssetsByType(Config.TEXT_ASSET_TYPE)
            .filter((asset) => asset.isEditable);
        let currentBounds: PIXI.Rectangle;
        const watermarkAsset = this.#getMediaAsset();
        if (watermarkAsset) {
            currentBounds = watermarkAsset._pixiContainer.getBounds();
        }
        return this.template.watermark.availablePositions.filter((position) => {
            const bounds = this._getBoundsForPosition(position, currentBounds);
            return !textAssets.some((textAsset) =>
                bounds.intersects(textAsset._pixiContainer.getBounds()),
            );
        });
    }

    /**
     * Since the position getter may return a value from the template, we need to explicitly
     * serialize the underlying value.
     */
    _positionCustomSave() {
        return this.rawPosition;
    }

    get opacity() {
        return this.template.watermark.opacity;
    }

    _getBoundsForPosition(
        position: WatermarkPositions,
        currentBounds?: PIXI.Rectangle,
    ): PIXI.Rectangle {
        let { maxWidth, maxHeight, transformation } = this._computeLayoutForPosition(position);
        if (currentBounds) {
            // If the current bounds of the watermark are known, then use those
            maxWidth = currentBounds.width;
            maxHeight = currentBounds.height;
        }
        const x = transformation.position.x - transformation.anchor.x * maxWidth;
        const y = transformation.position.y - transformation.anchor.y * maxHeight;
        return new PIXI.Rectangle(x, y, maxWidth, maxHeight);
    }

    /**
     * Find the position & size of the watermark for any position, based on Aspect Ratio.
     */
    _computeLayoutForPosition(position: SceneWatermarkPosition) {
        const resolution = getReferenceResolution(this.#getAspectRatio());

        const activeLayout = this.template.watermark.activeLayout;

        let transformation = null;

        switch (position) {
            case WatermarkPositions.TopLeft:
                transformation = {
                    position: {
                        x: activeLayout.marginLeft,
                        y: activeLayout.marginTop,
                    },
                    anchor: {
                        x: 0,
                        y: 0,
                    },
                };
                break;
            case WatermarkPositions.BottomLeft:
                transformation = {
                    position: {
                        x: activeLayout.marginLeft,
                        y: resolution.height - activeLayout.marginBottom,
                    },
                    anchor: {
                        x: 0,
                        y: 1,
                    },
                };
                break;
            case WatermarkPositions.TopRight:
                transformation = {
                    position: {
                        x: resolution.width - activeLayout.marginRight,
                        y: activeLayout.marginTop,
                    },
                    anchor: {
                        x: 1,
                        y: 0,
                    },
                };
                break;
            case WatermarkPositions.BottomRight:
                transformation = {
                    position: {
                        x: resolution.width - activeLayout.marginRight,
                        y: resolution.height - activeLayout.marginBottom,
                    },
                    anchor: {
                        x: 1,
                        y: 1,
                    },
                };
                break;
            case WatermarkPositions.BottomCenter:
                transformation = {
                    position: {
                        x: resolution.width / 2,
                        y: resolution.height - activeLayout.marginBottom,
                    },
                    anchor: {
                        x: 0.5,
                        y: 1,
                    },
                };
                break;
            case WatermarkPositions.Center:
                transformation = {
                    position: {
                        x: resolution.width / 2,
                        y: resolution.height / 2,
                    },
                    anchor: {
                        x: 0.5,
                        y: 0.5,
                    },
                };
                break;
            case WatermarkPositions.TopCenter:
                transformation = {
                    position: {
                        x: resolution.width / 2,
                        y: activeLayout.marginTop,
                    },
                    anchor: {
                        x: 0.5,
                        y: 0,
                    },
                };
                break;
            case WatermarkPositions.CenterLeft:
                transformation = {
                    position: {
                        x: activeLayout.marginLeft,
                        y: resolution.height / 2,
                    },
                    anchor: {
                        x: 0,
                        y: 0.5,
                    },
                };
                break;
            case WatermarkPositions.CenterRight:
                transformation = {
                    position: {
                        x: resolution.width - activeLayout.marginRight,
                        y: resolution.height / 2,
                    },
                    anchor: {
                        x: 1,
                        y: 0.5,
                    },
                };
                break;
            default:
                throw new Error(`Position is invalid: ${position}`);
        }

        return {
            maxWidth: activeLayout.maxWidth,
            maxHeight: activeLayout.maxHeight,
            transformation,
        };
    }

    /**
     * Find the position & size of the watermark, based on Aspect Ratio and Watermark Position.
     */
    _computeLayout() {
        return this._computeLayoutForPosition(this.position);
    }

    removeWatermarkIfExists() {
        const scene = this.parent as Scene;
        if (scene.hasAssetByName('watermark_1')) {
            const watermark = scene.getAssetByName('watermark_1');
            watermark.destroy(true);
        }
    }

    #hideWatermarkIfExists() {
        const scene = this.parent as Scene;
        if (scene.hasAssetByName('watermark_1')) {
            const watermark = scene.getAssetByName('watermark_1');
            if (watermark.sourceUrl === VideoData.INVISIBLE_IMAGE) {
                return;
            }

            watermark.sourceData = VideoData.INVISIBLE_IMAGE_SOURCE_DATA;
        }
    }

    override _update() {
        if (this.sourceData === Config.WATERMARK_NONE || !this.template.watermark.visible) {
            // No watermark or can't bump to any position
            this.#hideWatermarkIfExists();
            return;
        }

        const watermark = this.#getOrCreateMediaAsset();
        this.#updateMediaAssetSourceData(watermark);
        this.#updateMediaAssetTransform(watermark);
        // Update visibility after the source data and transform have been updated.
        // See #updateVisibility for more details.
        this.#updateVisibility(watermark);
        this.#updatePosition();

        // Ensure watermarks are always placed at the top of the scene's asset hierarchy.
        const scene = this.slide;
        if (scene && scene.scene && watermark) {
            const children = scene.scene.children;
            const watermarkIndex = children.indexOf(watermark);
            const lastIndex = children.length - 1;

            if (watermarkIndex !== -1 && watermarkIndex !== lastIndex) {
                scene.scene.moveChild(watermark, lastIndex);
            }
        }
    }

    #getOrCreateMediaAsset(): TimeAwareMediaAsset {
        const scene = this.slide;

        let watermark = this.#getMediaAsset();
        if (!watermark) {
            watermark = new TimeAwareMediaAsset(scene.scene, {
                name: 'watermark_1',
                updateEventSource: this,
                updateParentOnRedraw: true,
                autoDraw: (scene as Scene).autoDraw,
                timeline: scene.scene.root.timeline,
                fetchResources: this.fetchResources,
                audioMode: scene.scene.root.audioMode,
                assetNetworkManager: this.root._assetNetworkManager,
                getAspectRatio: () => this.#getAspectRatio(),
                seekWaitTime: scene.scene.root.settings.video.seekWaitTime,
                reusableTextures: scene.scene._reusableTextures,
                mediaSizeToUse: this.root.mediaSizeToUse,
            });
            watermark.kenburn = 'none';
            watermark.animationConfig = [];
            watermark.isEditable = false;
            watermark.mask = null;
            watermark.generateNewId();
            scene.scene.insertAsset(watermark);
        }

        return watermark;
    }

    #getMediaAsset(): TimeAwareMediaAsset | undefined {
        const scene = this.slide;
        return scene.getAssetByName('watermark_1', { deep: false });
    }

    #updateMediaAssetSourceData(watermark: TimeAwareMediaAsset) {
        if (!isSourceDataEqual(watermark.sourceData, this.sourceData as MediaSourceData)) {
            watermark.sourceData = {
                ...(this.sourceData as Object),
                type: Config.IMAGE_TYPE_BACKGROUND,
            } as MediaSourceData;
        }

        watermark.fillMode = this.template.watermark.fillMode;
        watermark.opacity = this.opacity;
    }

    #updateMediaAssetTransform(watermark: TimeAwareMediaAsset) {
        const { maxWidth, maxHeight, transformation } = this._computeLayout();

        watermark.transformation.position.load(transformation.position);
        watermark.transformation.anchor.load(transformation.anchor);
        watermark.maxWidth = maxWidth;
        watermark.maxHeight = maxHeight;
        watermark.applyTransformation();
    }

    #updatePosition() {
        /**
         * "Bump" to next available position if we can't display the watermark in the current position.
         * This can happen when a text asset begins overlapping a watermark's bounds.
         */
        const availablePositions = this.availablePositions;

        if (availablePositions.length === 0) {
            // Watermark cannot be displayed, position is irrelevant.
            return;
        }

        if (this.#positionBeforeBump !== null) {
            const positionBeforeBump =
                this.#positionBeforeBump === Config.INHERIT_FROM_TEMPLATE
                    ? this.#templateWatermarkPosition
                    : this.#positionBeforeBump;

            if (availablePositions.includes(positionBeforeBump)) {
                this.#position = this.#positionBeforeBump;
                this.#positionBeforeBump = null;
                return;
            }
        }

        if (!availablePositions.includes(this.position)) {
            if (this.#positionBeforeBump === null) {
                this.#positionBeforeBump = this.#position;
            }
            this.#position = availablePositions[0];
        }
    }

    #updateVisibility(watermark: TimeAwareMediaAsset) {
        // We toggle the watermark's visibility to preserve its boundaries for accurate overlap calculations,
        // as the availablePositions rely on these bounds. Removing the watermark when no positions are available
        // would cause a loss of the exact boundary data. This approach of hiding, rather than removing the
        // watermark, avoids disruptions in overlap calculations and potential infinite loops.
        watermark.visibleWithUpdate = this.availablePositions.length !== 0;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize
            .concat(['position'])
            .concat(getAttributesToSerialize(this));
    }
}
